import {Navigate, useLocation, useParams, useSearchParams} from 'react-router-dom';
import DashboardLayout from './layouts/DashboardLayout';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import Clients from "./pages/Clients";
import ClientPage from "./pages/ClientPage";
import NewClientPage from "./pages/NewClientPage";
import ClientSetupPage from "./pages/ClientSetupPage";
import ClientPageV2 from "./pages/ClientPageV2";
import Companies from "./pages/Companies";
import CompanyGroupAdmins from "./pages/CompanyGroupAdmins";
import Managers from "./pages/Managers";
import ManagerPermissionsPage from "./pages/ManagerPermissionsPage";
import CompanyGroupAdminPage from "./pages/CompanyGroupAdminPage";
import Dashboard from "./pages/Dashboard";
import ClientNutritionV2Page from "./pages/ClientNutritionV2Page";
import MenuBuilderV2Page from "./pages/MenuBuilderV2Page";
import SavedMealsPage from "./pages/SavedMealsPage";
import MenuTemplatesPage from "./pages/MenuTemplatesPage";
import MenuTemplateBuilderPage from "./pages/MenuTemplateBuilderPage";
import ClientWeightTrackPage from "./pages/ClientWeightTrackPage";
import CompanyPage from "./pages/CompanyPage";
import Exercises from "./pages/Exercises";
import Trainers from "./pages/Trainers";
import TrainerPage from "./pages/TrainerPage";
import SettingPage from "./pages/SettingPage";
import AutomationFlowPage from "./pages/AutomationFlowPage";
import AutomationV2Page from "./pages/AutomationV2Page";
import ClientWeekPage from "./pages/ClientWeekPage";
import ClientWeekOverviewPage from "./pages/ClientWeekOverviewPage";
import AlertsPage from "./pages/AlertsPage";
import FormsPage from "./pages/FormsPage";
import NotificationsPage from "./pages/NotificationsPage";
import ProgramRenew from "./pages/ProgramRenew";
import AdminCoupons from "./pages/AdminCoupons"; // Add this import
import AdminPartnerships from "./pages/AdminPartnerships";
import Marketplace from "./pages/Marketplace";
import AdminMessages from "./pages/AdminMessages";
import BuildConsolePage from "./pages/BuildConsolePage";
import AuditLogPage from "./pages/AuditLogPage";
import Page404 from "./pages/Page404";
import {useAuth} from "./contexts/AuthContext";
import NewTemplatePage from "./pages/NewTemplatePage";
import TemplatePresentPage from "./pages/TemplatePresentPage";
import NewTemplates from "./pages/NewTemplates";
import ExercisePage from "./pages/ExercisePage";
import FoodDictionaryScreen from "./pages/FoodDictionaryScreen";
import FoodDictionaryPage from "./pages/FoodDictionaryPage";
import GuidesScreen from "./pages/GuidesScreen";
import RecipesScreen from "./pages/RecipesScreen";
import SupplementsScreen from "./pages/SupplementsScreen";
import SupplementPage from "./pages/SupplementPage";
import MeasurementDefinitionsScreen from "./pages/MeasurementDefinitionsScreen";
import MeasurementDefinitionPage from "./pages/MeasurementDefinitionPage";
import BodyMeasurementsTablePage from "./pages/BodyMeasurementsTablePage";
import ImportUsersPage from "./pages/ImportUsersPage";
import ImportTemplatePage from "./pages/ImportTemplatePage";
import TasksPage from "./pages/TasksPage";
import ProgressPhotosPage from "./pages/ProgressPhotosPage";
import ProgressVideosPage from "./pages/ProgressVideosPage";
import CheckInsPage from "./pages/CheckInsPage";
import CheckInDetailPage from "./pages/CheckInDetailPage";
import PermissionGuard from "./guards/PermissionGuard";
import PresentationLayout from "./layouts/PresentationLayout";

/**
 * `/nutrition-v2` -> `/nutrition`, keeping the client id AND the query string.
 *
 * A component rather than a bare <Navigate>, because the target has to interpolate the route param
 * and `Navigate` takes a literal path.
 *
 * Carrying `location.search` is not tidiness. `?templateId=` is how a menu is seeded from a
 * template, and rebuilding the URL from the route params alone SILENTLY dropped it: the trainer
 * picked a template, the builder opened with no template, and the menu they got was empty. It
 * looked like the template had no foods in it.
 */
function RedirectToNutrition() {
  const {userId} = useParams();
  const {search} = useLocation();
  return <Navigate to={`/app/clients/${userId}/nutrition${search}`} replace/>;
}

function RedirectToNutritionMenu() {
  const {userId, menuId} = useParams();
  const {search} = useLocation();
  return <Navigate to={`/app/clients/${userId}/nutrition/menu/${menuId}${search}`} replace/>;
}

/**
 * ספריית תזונה was one page with two tabs; each tab is its own page now. The tab that was in the
 * query string decides which one, so a link a trainer shared to the templates tab still opens
 * templates rather than dropping them on the other library.
 */
function RedirectToLibrary() {
  const [params] = useSearchParams();
  return <Navigate to={params.get('tab') === 'templates' ? '/app/menu-templates' : '/app/saved-meals'} replace/>;
}

function NavigateGuard() {
  const {isAdmin, isCompanyGroupAdmin} = useAuth();
  // Admins and the company-less group-admin persona land on the (system-aware) dashboard; only
  // company-scoped roles (trainers) default to the clients list.
  return (isAdmin() || isCompanyGroupAdmin())
      ? <Navigate to="/app/dashboard"/>
      : <Navigate to="/app/clients" />;
}

// Every /app screen, before permission wrapping. DashboardLayout's <AuthGuard> already answered
// "is anyone logged in"; these are wrapped below so routing also answers "is this screen theirs",
// which nothing used to ask (see PermissionGuard).
const appScreens = [
  { path: 'dashboard', element: <Dashboard /> },
  { path: 'templates', element: <NewTemplates /> },
  // The legacy catalog screen and its detail page are gone; /app/food-dictionary is the only one
  // now. Redirects rather than nothing, because trainers have these URLs bookmarked.
  { path: 'food', element: <Navigate to="/app/food-dictionary" replace /> },
  { path: 'foods/:foodId', element: <Navigate to="/app/food-dictionary" replace /> },
  { path: 'food-dictionary', element: <FoodDictionaryScreen /> },
  { path: 'food-dictionary/:foodId', element: <FoodDictionaryPage /> },
  { path: 'supplements', element: <SupplementsScreen /> },
  { path: 'supplements/:supplementId', element: <SupplementPage /> },
  { path: 'company-measurements', element: <MeasurementDefinitionsScreen /> },
  { path: 'company-measurements/:id', element: <MeasurementDefinitionPage /> },
  { path: 'guides', element: <GuidesScreen /> },
  { path: 'recipes', element: <RecipesScreen /> },
  // { path: 'new_template/', element: <NewTemplates />},
  // { path: 'new_template/:templateId', element: <NewTemplatePage />},
  { path: 'templates/:templateId', element: <NewTemplatePage /> },
  { path: 'exercises', element: <Exercises /> },
  { path: 'exercises/:exerciseId', element: <ExercisePage /> },
  { path: 'coupons', element: <AdminCoupons /> }, // Add this route
  { path: 'partnerships', element: <AdminPartnerships /> },
  { path: 'marketplace', element: <Marketplace /> },
  { path: 'admin-messages', element: <AdminMessages /> },
  { path: 'audit-log', element: <AuditLogPage /> },
  { path: 'companies', element: <Companies /> },
  { path: 'managers', element: <Managers /> },
  { path: 'managers/:managerId/permissions', element: <ManagerPermissionsPage /> },
  { path: 'administrators', element: <CompanyGroupAdmins /> },
  { path: 'administrators/:adminId', element: <CompanyGroupAdminPage /> },
  { path: 'trainers', element: <Trainers /> },
  { path: 'trainers/:trainerId', element: <TrainerPage /> },
  { path: 'companies/:companyId', element: <CompanyPage /> },
  { path: 'alerts', element: <AlertsPage/>},
  { path: 'forms', element: <FormsPage/>},
  { path: 'notifications', element: <NotificationsPage/>},
  { path: 'tasks', element: <TasksPage/>},
  { path: 'import-users', element: <ImportUsersPage/>},
  { path: 'import-template', element: <ImportTemplatePage/>},
  { path: 'check-ins', element: <CheckInsPage/>},
  { path: 'check-ins/:checkInId', element: <CheckInDetailPage/>},
  { path: 'build-console', element: <BuildConsolePage/>},
  { path: 'program_renew', element: <ProgramRenew/>},
  { path: 'setting', element: <SettingPage /> },
  { path: 'automations', element: <AutomationV2Page /> },
  { path: 'automations-flow', element: <AutomationFlowPage /> },
  { path: 'automations-v2', element: <AutomationV2Page /> },
  { path: 'clients', element: <Clients /> },
  // Static segments outrank dynamic ones in react-router 6, so 'clients/new' wins over
  // 'clients/:userId'. Neither path needs a NAVIGATION entry: resolveScreen longest-prefix
  // matches, so both inherit `/app/clients` (no roles = every clients-capable staff role).
  { path: 'clients/new', element: <NewClientPage /> },
  { path: 'clients/:userId/setup', element: <ClientSetupPage /> },
  { path: 'clients/:userId', element: <ClientPage /> },
  { path: 'clients/:userId/v2', element: <ClientPageV2 /> },
  { path: 'clients/:userId/:programId/week/:blockIndex/:weekIndex', element: <ClientWeekPage /> },
  { path: 'clients/:userId/:programId/week/:blockIndex/:weekIndex/overview', element: <ClientWeekOverviewPage /> },
  // V2 IS the nutrition page now. /nutrition is the canonical path and serves it, so every existing
  // link, bookmark and deep link from the dashboard lands on the new builder without each of them
  // having to be found and rewritten.
  { path: 'clients/:userId/nutrition', element: <ClientNutritionV2Page /> },
  { path: 'clients/:userId/nutrition/menu/:menuId', element: <MenuBuilderV2Page /> },
  // The paths V2 lived on while it was the alternative. Kept as redirects for the tabs trainers
  // already have open and the links they shared with each other.
  { path: 'clients/:userId/nutrition-v2', element: <RedirectToNutrition /> },
  { path: 'clients/:userId/nutrition-v2/menu/:menuId', element: <RedirectToNutritionMenu /> },
  // The V1 page lived here, read-only, while the two could be compared. It is gone: nothing writes
  // or reads V1 nutrition any more, so a screen showing those rows could only mislead.
  { path: 'clients/:userId/nutrition-legacy', element: <RedirectToNutrition /> },
  // The company libraries, one page each. They were tabs of a single ספריית תזונה page, which put
  // two different jobs behind one heading — a template is a whole menu you build and open, a saved
  // meal is one meal you reuse — and left the nav pointing at a query string.
  { path: 'saved-meals', element: <SavedMealsPage /> },
  { path: 'menu-templates', element: <MenuTemplatesPage /> },
  // The same builder a menu is built in, without the parts that belong to one trainee.
  { path: 'menu-templates/:templateId', element: <MenuTemplateBuilderPage /> },
  // Where those two used to live. `replace` so Back does not bounce between the two.
  { path: 'nutrition-library', element: <RedirectToLibrary /> },
  // The two V1 menu builders lived here. A V1 menu and its V2 copy are different rows, so these ids
  // cannot be redirected to the equivalent menu — they land on the trainee's nutrition page instead,
  // which is where the copy actually is.
  { path: 'clients/:userId/menu/:menuId', element: <RedirectToNutrition /> },
  { path: 'clients/:userId/menu-v2/:menuId', element: <RedirectToNutrition /> },
  // The V1 global templates, whose templates V20260902.1 copied into V2.
  { path: 'global-templates', element: <Navigate to="/app/menu-templates" replace /> },
  // Not to the equivalent template, for the same reason: the id would open the wrong one.
  { path: 'global-templates/:templateId', element: <Navigate to="/app/menu-templates" replace /> },
  { path: 'clients/:userId/weight', element: <ClientWeightTrackPage /> },
  { path: 'clients/:userId/measurements', element: <BodyMeasurementsTablePage /> },
  { path: 'clients/:userId/measurements/:measurementType', element: <BodyMeasurementsTablePage /> },
  { path: 'clients/:userId/progress-photos', element: <ProgressPhotosPage /> },
  { path: 'clients/:userId/progress-videos', element: <ProgressVideosPage /> },
];

const routes = [
  {
    path: 'app',
    element: <DashboardLayout />,
    children: [
      // One wrapper for every screen — the guard reads the CURRENT pathname off the router and looks
      // it up in the navigation registry, so there is no per-route permission argument to forget or
      // let drift out of sync with the side nav.
      ...appScreens.map(({ path, element }) => ({
        path,
        element: <PermissionGuard>{element}</PermissionGuard>,
      })),
      // The catch-all is a redirect, not a screen — nothing to permit.
      { path: '*', element: <Navigate to="/404" /> }
    ]
  },
  // Studio display mode: ONE workout of a program, on a screen the trainees read. Deliberately a
  // TOP-LEVEL route and not an /app screen — it opens in its own tab, with no side nav and no top
  // bar, and the trainer keeps working in the tab they opened it from. See PresentationLayout.
  {
    path: 'present',
    element: <PresentationLayout />,
    children: [
      { path: 'templates/:templateId/:dayIndex', element: <TemplatePresentPage /> },
      { path: 'templates/:templateId', element: <TemplatePresentPage /> },
    ]
  },
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { path: 'login', element: <Login /> },
      { path: '/', element: <NavigateGuard/> },
      { path: '*', element: <Page404 to="/404" /> }
    ]
  }
];

export default routes;